import React from 'react';
import { Idea, Priority, Topic } from '../types';
import { Circle, Edit2, RotateCcw } from 'lucide-react';

interface IdeaListProps {
  ideas: Idea[];
  topics: Topic[];
  onToggleStatus: (id: string, currentStatus: boolean) => void;
  onEdit: (idea: Idea) => void;
  readOnly?: boolean;
}

const PriorityBadge: React.FC<{ priority: Priority }> = ({ priority }) => {
  const colors = {
    [Priority.HIGH]: 'bg-red-50 text-red-600 border-red-100',
    [Priority.MEDIUM]: 'bg-yellow-50 text-yellow-600 border-yellow-100',
    [Priority.LOW]: 'bg-green-50 text-green-600 border-green-100',
  };

  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${colors[priority]} font-bold uppercase tracking-wide`}>
      {priority}
    </span>
  );
};

export const IdeaList: React.FC<IdeaListProps> = ({ ideas, topics, onToggleStatus, onEdit, readOnly = false }) => {
  if (ideas.length === 0) {
    return (
      <div className="text-center py-10 text-gray-400 bg-white rounded-xl border border-dashed border-gray-200">
        <p className="text-sm">No ideas found.</p>
      </div>
    );
  }

  const getTopicName = (id: string) => topics.find(t => t.id === id)?.name || 'Unknown';

  return (
    <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {ideas.map((idea) => {
        // Only apply dimming and strikethrough if it's DONE and NOT in readOnly (Archive) mode.
        // If readOnly is true (Archive view), we want it to look normal/readable.
        const isCompletedView = idea.isDone && !readOnly;

        return (
          <div 
            key={idea.id} 
            className={`group bg-white rounded-lg shadow-sm border border-gray-200 p-3 transition hover:shadow-md hover:border-gray-300 flex flex-col justify-between h-full ${isCompletedView ? 'opacity-60 bg-gray-50' : ''}`}
          >
            <div>
              <div className="flex justify-between items-start mb-2">
                <PriorityBadge priority={idea.priority} />
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-100 px-1.5 py-0.5 rounded">
                  {getTopicName(idea.topicId)}
                </span>
              </div>
              
              <h3 className={`font-bold text-sm text-gray-800 mb-1 leading-snug line-clamp-2 ${isCompletedView ? 'line-through text-gray-500' : ''}`}>
                {idea.title}
              </h3>
              
              <p className="text-xs text-gray-500 mb-3 line-clamp-2 min-h-[2.5em]">
                {idea.description || "No description provided."}
              </p>
            </div>

            <div className="flex justify-between items-center pt-2 border-t border-gray-50 mt-auto">
              {!readOnly ? (
                <button
                  onClick={() => onToggleStatus(idea.id, idea.isDone)}
                  className="flex items-center space-x-1.5 text-xs text-gray-500 hover:text-green-600 transition-colors"
                >
                  <Circle size={16} />
                  <span>Mark Done</span>
                </button>
              ) : (
                  <button
                  onClick={() => onToggleStatus(idea.id, idea.isDone)}
                  className="flex items-center space-x-1.5 text-xs text-gray-500 hover:text-primary transition-colors"
                  title="Restore to active list"
                >
                  <RotateCcw size={16} />
                  <span>Restore</span>
                </button>
              )}
              
              {!readOnly && (
                  <button 
                    onClick={() => onEdit(idea)}
                    className="p-1 text-gray-300 hover:text-primary hover:bg-red-50 rounded transition-colors"
                  >
                    <Edit2 size={14} />
                  </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};